import { Collapse } from '@sgme/ui';
import { useAppSelector } from 'hooks/useAppSelector';
import type { IntlKey } from 'locales';
import { PropsWithChildren, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { hasBasketOptimizer, isBasketOptimizerAccordionOpened } from 'state/selectors/common/uiPanel';
import { toggleBasketOptimizerAccordion } from 'state/uiPanelState/advisory/advisory.action';

import { BasketOptimizer } from './basket-optimizer';

type ItemKey = 'basket-optimizer';
type Props = {
  tabId: string;
  quoteId: string;
};
export function Advisory({ tabId, quoteId }: Props) {
  const dispatch = useDispatch();
  const isBasketOptimizerOpened = useAppSelector(s => isBasketOptimizerAccordionOpened(s, tabId));
  const displayBasketOptimizer = useAppSelector(s => hasBasketOptimizer(s, quoteId));
  const toggleBasketOptimizer = () => dispatch(toggleBasketOptimizerAccordion(tabId));
  const defaultActiveKey = displayBasketOptimizer ? 'basket-optimizer' : null;

  if (defaultActiveKey === null) {
    return null;
  }

  const onSelect = (itemKey: Nullable<ItemKey>) => {
    if (itemKey === 'basket-optimizer') {
      toggleBasketOptimizer();
    }
  };

  const selected: ItemKey[] = [];
  if (isBasketOptimizerOpened) {
    selected.push('basket-optimizer');
  }

  return (
    <div className="flex-grow-1 overflow-y-auto">
      <Collapse
        fill="fill"
        icon="end"
        size="sm"
        activeKey={selected}
        onSelect={itemKey => onSelect(itemKey as Nullable<ItemKey>)}
      >
        {displayBasketOptimizer && (
          <AdvisoryComponentWrapper titleId="basketOptimizer.optimiseMyUnderlyingUniverse" e2e="basket-optimizer">
            <BasketOptimizer tabId={tabId} />
          </AdvisoryComponentWrapper>
        )}
      </Collapse>
    </div>
  );
}

type AdvisoryComponentWrapperProps = PropsWithChildren<{
  children: ReactNode;
  titleId: IntlKey;
  e2e: string;
}>;

export function AdvisoryComponentWrapper({ children, titleId, e2e }: AdvisoryComponentWrapperProps) {
  return (
    <Collapse.Item itemKey={e2e} className="pb-2 pt-1 card-body">
      <Collapse.Header className="py-2" data-e2e={e2e}>
        <FormattedMessage id={titleId} defaultMessage={titleId} />
      </Collapse.Header>
      <Collapse.Body>{children}</Collapse.Body>
    </Collapse.Item>
  );
}
